import { Box, Button, Card, CardContent, CardHeader, Chip, DialogContentText, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { apiConstants, post } from '../../../common/constants'
import * as React from 'react'
import { useSnackbar } from "notistack";
import MUIModal from "../../../components/MuiModal";
import { DataGrid } from "@mui/x-data-grid";
import KYCUpload from "../KYCUpload";
import { formatDate, getDocumentURLSync } from "../../../common/util";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadIcon from '@mui/icons-material/Upload';

// `setReload` comes from the page-level merchant fetch (index.js → DetailsTab) — calling
// it re-fetches the merchant so the KYC grid reflects the just-submitted verdicts.
function KycDetails({ data, id, setReload }) {
    const [dialog, setDialog] = useState(false)
    // Local verdicts for the pending documents, keyed by kycDocName: 'approved' | 'rejected'.
    // Collected here and submitted together in a single request so the backend evaluates
    // the complete review atomically (one document per request races the status transition).
    const [verdicts, setVerdicts] = useState({})
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const { enqueueSnackbar } = useSnackbar();
    // A link ending in #kyc (e.g. from PTL Merchant Setup) opens this card directly, once the merchant has loaded.
    const kycRef = React.useRef(null);
    const scrolledToKyc = React.useRef(false);
    React.useEffect(() => {
        if (scrolledToKyc.current || window.location.hash !== '#kyc' || !data || !kycRef.current) return;
        scrolledToKyc.current = true;
        kycRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [data]);
    const handleClose = () => {
        setDialog(false)
        setConfirmOpen(false)
    };

    // Toggle a verdict on/off for a single document (does not hit the API).
    const setVerdict = (docName, value) => {
        setVerdicts((prev) => {
            const next = { ...prev };
            if (prev[docName] === value) delete next[docName];
            else next[docName] = value;
            return next;
        });
    };

    // Submit every marked verdict as ONE batched request to verifyKycDocuments.
    const submitReview = () => {
        const payload = Object.entries(verdicts).map(([documentName, value]) =>
            value === 'rejected' ? { documentName, rejected: true } : { documentName }
        );
        if (payload.length === 0) return;
        setSubmitting(true);
        post(`${apiConstants.KYC_APPROVAL}/${id}`, payload).then((res) => {
            if (res.status === 200) {
                const approved = payload.filter((p) => !p.rejected).length;
                const rejected = payload.length - approved;
                enqueueSnackbar(
                    `Review submitted — ${approved} approved, ${rejected} rejected`,
                    { variant: rejected > 0 ? 'warning' : 'success' }
                );
                setVerdicts({});
                setReload((r) => r + 1);
            }
        }).catch((err) => {
            console.log(err);
            enqueueSnackbar(
                err?.response?.data?.message || 'Something went wrong while submitting the review',
                { variant: 'error' }
            );
        }).finally(() => {
            setSubmitting(false);
            setConfirmOpen(false);
        });
    }

    const columns2 = [
        {
            field: 'kycDocName',
            headerName: 'Document Name',
            flex: 1,
        },
        {
            field: 'docUrl',
            headerName: 'Link',
            flex: 1,
            renderCell: (col) => {
                // return (
                //     <a href={col.row.docUrl} target="_blank" rel="noreferrer">View</a>
                //     <Button onClick={() => openDocument(order?.invoice_url, 'INVOICE')} startIcon={<ReceiptIcon />}>
                //     Invoice
                // </Button>
                // )
                return (
                    <Button
                        onClick={async () =>
                            window.open(
                                await getDocumentURLSync(
                                    col.row.docUrl,
                                    "_blank"
                                )
                            )
                        }
                        style={{ marginRight: "8px", background: "whitesmoke" }}
                        size={'small'}
                        className='kt-subheader__breadcrumbs-home'>
                        View
                    </Button>
                )
            }
        },
        {
            field: 'verified',
            headerName: 'Verified',
            flex: 1,
            renderCell: (col) => {
                if (col.row.rejected === true) {
                    return (<Tooltip title="Rejected">
                        <IconButton color='error'>
                            <HighlightOffIcon />
                        </IconButton>
                    </Tooltip>)
                }
                if (col.row.verified === false) {
                    return (
                        <Tooltip title="Pending">
                            <IconButton color='warning'>
                                <PendingIcon />
                            </IconButton>
                        </Tooltip>
                    )
                }
                else {
                    return (
                        <Tooltip title="Approved">
                            <IconButton color="success">
                                <CheckCircleIcon />
                            </IconButton>
                        </Tooltip>
                    )
                }
            }
        },

        {
            field: 'active',
            headerName: 'Active',
            flex: 0.6,
            renderCell: (params) => {
                if (params.row.active === true) {
                    return "Yes"
                }
                else {
                    return "No"
                }
            }
        },

        {
            field: 'uploadDate',
            headerName: 'Uploaded Date',
            flex: 1,
            renderCell: (col) => formatDate(col.row.uploadDate)
        },

        {
            field: 'verifiedDate',
            headerName: 'Reviewed Date',
            flex: 1,
            renderCell: (col) => formatDate(col.row.verifiedDate)
        },
        {
            field: 'verifiedBy',
            headerName: 'Reviewed By',
            flex: 1,
            renderCell: (col) => (
                <Typography variant='body2' sx={{fontSize: '12px'}}>
                    {col.row.verifiedBy || '—'}
                </Typography>
            ),
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 1.5,
            renderCell: (col) => {
                if (col.row.verified === false && col.row.rejected !== true) {
                    const current = verdicts[col.row.kycDocName];
                    return (
                        <>
                            <Button
                                variant={current === 'approved' ? 'contained' : 'outlined'}
                                color='success'
                                onClick={() => setVerdict(col.row.kycDocName, 'approved')}
                            >Approve</Button>
                            <Button
                                variant={current === 'rejected' ? 'contained' : 'outlined'}
                                sx={{ ml: '10px' }}
                                color='error'
                                onClick={() => setVerdict(col.row.kycDocName, 'rejected')}
                            >Reject</Button>
                        </>
                    )
                }
            }
        },
    ]
    const status = (data && data?.status ? data?.status : "No status Found")
    const color = status === 'KYC_APPROVED' ? "success" : "error";
    const handleClick = () => {
        setDialog(true);
    }
    return (
        <div>
            <Grid sx={{ paddingBottom: 2 }}>
                <Card id="kyc" ref={kycRef} sx={{ scrollMarginTop: 80 }}>
                    <CardHeader title={
                        <Typography variant="h6">
                            {'KYC Details'}
                        </Typography>}
                    />
                    <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", paddingBottom: 2 }}>
                            <Typography variant="subtitle2" sx={{ paddingBottom: 1 }}>Status :
                                <Typography variant='subtitle2' component="span" sx={{ paddingLeft: 1 }}><Chip color={color} label={status} /></Typography></Typography>
                            <Button variant="contained" size="small" onClick={handleClick} sx={{ boxShadow: 0 }}
                                startIcon={<UploadIcon size={'smaller'} />}>Upload</Button>
                            <MUIModal open={dialog} handleClose={handleClose} children={<KYCUpload id={id} setReload={setReload} setDialog={setDialog} dialog={dialog} status={status} />}
                                title={"Please fill the below mentioned details"}
                                child={<Typography variant="body2" color='red'>File must be in .pdf, .png, .jpg, .jpeg format</Typography>} />
                        </Box>

                        {data?.kycDetails?.length > 0 &&
                            <DataGrid
                                columns={columns2}
                                getRowId={(row) => Math.random()}
                                rows={data.kycDetails}
                                autoHeight={true}
                                hideFooterPagination={true}
                            // disableColumnMenu
                            // disableColumnFilter
                            // disableSelectionOnClick
                            />
                        }

                        {data?.kycDetails?.some((d) => d.verified === false && d.rejected !== true) && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, pt: 2 }}>
                                <Typography variant='body2' color='text.secondary'>
                                    {Object.keys(verdicts).length} of {data.kycDetails.filter((d) => d.verified === false && d.rejected !== true).length} pending document(s) marked
                                </Typography>
                                <Button
                                    variant='contained'
                                    disabled={Object.keys(verdicts).length === 0 || submitting}
                                    onClick={() => setConfirmOpen(true)}
                                >Submit Review</Button>
                            </Box>
                        )}

                        <MUIModal
                            open={confirmOpen}
                            handleClose={handleClose}
                            title={'Submit KYC review?'}
                            children={
                                <DialogContentText id="alert-dialog-description">
                                    I confirm that I have reviewed the authenticity of the documents submitted. The following verdicts will be sent together in a single review:
                                    {Object.entries(verdicts).map(([name, v]) => (
                                        <Box component="span" sx={{ display: 'block', mt: 0.5 }} key={name}>
                                            • {name} — <b style={{ color: v === 'rejected' ? '#d32f2f' : '#2e7d32' }}>{v === 'rejected' ? 'Reject' : 'Approve'}</b>
                                        </Box>
                                    ))}
                                </DialogContentText>
                            }
                            action={
                                <Button variant="contained" color='success' disabled={submitting} onClick={submitReview} autoFocus>
                                    Confirm &amp; Submit
                                </Button>
                            }
                        />

                    </CardContent>
                </Card>
            </Grid>
        </div >
    );
}


export default KycDetails;
